import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [labSlideIndex, setLabSlideIndex] = useState(0);
  const [awardSlideIndex, setAwardSlideIndex] = useState(0);

  // Award Ceremony slideshow images
  const awardImages = [
    {
      src: "https://customer-assets.emergentagent.com/job_codemustakim/artifacts/02yv8qrd_477001555_122195088830087677_7021196249661981997_n.jpg",
      title: "Award Ceremony",
      description: "British Council IELTS Top Performer recognition"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_codemustakim/artifacts/ix5qmawy_476444711_122194302932087677_4851382869300624779_n.jpg",
      title: "Award Ceremony",
      description: "Award of Excellence 2024-25 - Radiance Education"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_codemustakim/artifacts/8mmuqlf7_599122555_122241940304087677_6092597597489986775_n.jpg",
      title: "Award Ceremony",
      description: "Top Performer Award - IELTS Partners Meet 2025-26"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_codemustakim/artifacts/vzcyjf97_95EA0B42-7FC5-473D-B1FA-664C3EaCC3DC5.jpeg",
      title: "Award Ceremony",
      description: "Receiving Top Performer Award from British Council"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_codemustakim/artifacts/dmw3hexy_7581.webp",
      title: "Award Ceremony",
      description: "British Council IELTS Partnership Programme"
    },
  ];

  // Computer Lab Session slideshow images
  const labImages = [
    {
      src: "https://customer-assets.emergentagent.com/job_codemustakim/artifacts/lpi5zdqw_IMG_3365.JPG.jpeg",
      title: "Computer Lab Session",
      description: "Students during computer security session"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_codemustakim/artifacts/j83agotp_IMG_3363.JPG.jpeg",
      title: "Computer Lab Session",
      description: "Students during computer security session"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_codemustakim/artifacts/d2id8xa2_IMG_3378.JPG%20%281%29.jpeg",
      title: "Computer Lab Session",
      description: "Students during computer security session"
    },
  ];

  // Auto-slide for award images every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setAwardSlideIndex((prev) => (prev + 1) % awardImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [awardImages.length]);

  // Auto-slide for lab images every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setLabSlideIndex((prev) => (prev + 1) % labImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [labImages.length]);

  const galleryImages = [
    {
      type: "slideshow",
      images: awardImages,
      currentIndex: awardSlideIndex,
      title: "Award Ceremony",
      description: "British Council IELTS Top Performer recognition"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_codemustakim/artifacts/cq281vms_WhatsApp%20Image%202026-01-26%20at%209.27.37%20PM.jpeg",
      title: "Classroom Session",
      description: "Engaging with students at Radiance Education"
    },
    {
      type: "slideshow",
      images: labImages,
      currentIndex: labSlideIndex,
      title: "Computer Lab Session",
      description: "Students during computer security session"
    },
  ];

  const openLightbox = (index, slideIndex = 0) => {
    const image = galleryImages[index];
    if (image.type === 'slideshow') {
      // For slideshow, set the current slide image
      setSelectedImage({
        index,
        isSlideshow: true,
        slideIndex,
        images: image.images
      });
    } else {
      setSelectedImage({ index, isSlideshow: false });
    }
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    if (selectedImage.isSlideshow) {
      setSelectedImage(prev => ({
        ...prev,
        slideIndex: (prev.slideIndex + 1) % prev.images.length
      }));
    } else {
      const nextIndex = (selectedImage.index + 1) % galleryImages.length;
      const nextImg = galleryImages[nextIndex];
      if (nextImg.type === 'slideshow') {
        setSelectedImage({
          index: nextIndex,
          isSlideshow: true,
          slideIndex: 0,
          images: nextImg.images
        });
      } else {
        setSelectedImage({ index: nextIndex, isSlideshow: false });
      }
    }
  };

  const prevImage = () => {
    if (selectedImage.isSlideshow) {
      setSelectedImage(prev => ({
        ...prev,
        slideIndex: (prev.slideIndex - 1 + prev.images.length) % prev.images.length
      }));
    } else {
      const prevIndex = (selectedImage.index - 1 + galleryImages.length) % galleryImages.length;
      const prevImg = galleryImages[prevIndex];
      if (prevImg.type === 'slideshow') {
        setSelectedImage({
          index: prevIndex,
          isSlideshow: true,
          slideIndex: 0,
          images: prevImg.images
        });
      } else {
        setSelectedImage({ index: prevIndex, isSlideshow: false });
      }
    }
  };

  const getCurrentLightboxImage = () => {
    if (!selectedImage) return null;
    if (selectedImage.isSlideshow) {
      return selectedImage.images[selectedImage.slideIndex];
    }
    return galleryImages[selectedImage.index];
  };

  return (
    <section id="gallery" className="py-20 lg:py-32 relative">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      
      {/* Background accent */}
      <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      {/* Light theme decorative elements */}
      <div className="light-decor-blob-3" style={{ top: '5%', right: '5%', width: '250px', height: '250px' }} />
      <div className="light-decor-blob-2" style={{ bottom: '10%', left: '-50px', width: '220px', height: '220px' }} />
      <div className="light-decor-circle-1" style={{ top: '20%', left: '5%', width: '70px', height: '70px' }} />
      <div className="light-decor-dot-1" style={{ top: '8%', right: '30%' }} />
      <div className="light-decor-line-1" style={{ top: '12%', left: '8%', width: '45px' }} />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium text-primary uppercase tracking-wider">Gallery</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 mb-4">
            Moments of <span className="text-gradient">Impact</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Capturing achievements, recognition, and the journey of building Radiance Education
          </p>
        </motion.div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 max-w-5xl mx-auto">
          {galleryImages.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ y: -8 }}
              onClick={() => {
                if (image.type === 'slideshow') {
                  openLightbox(index, image.currentIndex);
                } else {
                  openLightbox(index);
                }
              }}
              className="relative group cursor-pointer overflow-hidden rounded-2xl aspect-[4/3]"
            >
              {/* Image - Handle slideshow type */}
              {image.type === 'slideshow' ? (
                <div className="relative w-full h-full">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={image.currentIndex}
                      src={image.images[image.currentIndex].src}
                      alt={image.title}
                      loading="lazy"
                      decoding="async"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.8 }}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </AnimatePresence>
                  {/* Slide indicators */}
                  <div className="absolute top-3 right-3 z-20 flex gap-1">
                    {image.images.map((_, dotIndex) => (
                      <div
                        key={dotIndex}
                        className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                          dotIndex === image.currentIndex 
                            ? 'bg-primary w-4' 
                            : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <img
                  src={image.src}
                  alt={image.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              )}
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-300" />
              
              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                <h3 className="text-base font-semibold text-foreground mb-1">{image.title}</h3>
                <p className="text-sm text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-medium">{image.description}</p>
              </div>

              {/* Hover border effect */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-primary/50 rounded-2xl transition-colors duration-300" />
            </motion.div>
          ))}
        </div>

        {/* Lightbox */}
        <AnimatePresence>
          {selectedImage !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-lg"
              onClick={closeLightbox}
            >
              {/* Close button */}
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 z-50"
                onClick={closeLightbox}
              >
                <X className="w-6 h-6" />
              </Button>

              {/* Navigation buttons */}
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 z-50"
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
              >
                <ChevronLeft className="w-8 h-8" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 z-50"
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
              >
                <ChevronRight className="w-8 h-8" />
              </Button>

              {/* Image */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="max-w-4xl max-h-[80vh] mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                {getCurrentLightboxImage() && (
                  <>
                    <img
                      src={getCurrentLightboxImage().src}
                      alt={getCurrentLightboxImage().title}
                      loading="eager"
                      decoding="async"
                      className="w-full h-full object-contain rounded-2xl"
                    />
                    <div className="text-center mt-4">
                      <h3 className="text-lg font-semibold text-foreground">{getCurrentLightboxImage().title}</h3>
                      <p className="text-base text-muted-foreground font-medium">{getCurrentLightboxImage().description}</p>
                      {selectedImage.isSlideshow && (
                        <p className="text-xs text-muted-foreground mt-2">
                          {selectedImage.slideIndex + 1} / {selectedImage.images.length}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export { GallerySection };
